import { useTranslation } from "react-i18next";
import {
  IconArrowUpRight,
  IconCopy,
  IconHeadphones,
  IconLock,
  IconLockOpen,
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
} from "@tabler/icons-react";
import ShareV2Icon from "../../../assets/icons/share-v2.svg?react";
import { Button } from "../../../components/ui/Button";
import { Tag } from "../../../components/ui/Tag";
import { displayToast } from "../../../components/app/AppToast";
import { DesktopSaveButton } from "../../../components/save/DesktopSaveButton";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import { getArticleTypeText } from "../../../domains/app/utils/getArticleTypeText";
import {
  renderArticleDate,
  renderDate,
} from "../../../domains/app/utils/renderDate";
import { authorLine } from "../../../domains/article/utils/authorLine";
import { bareDoi } from "../../../domains/article/utils/sourceUrl";
import { cn } from "../../../lib/utils";
import { formatTime } from "../../../tools/utils";
import type { ArticlePageArticle } from "../../../features/article/hooks/useArticleData";
import { openExternalUrl } from "../../../tools/open-external-url";
import { SourceLabel } from "../../../components/ui/SourceLabel";

/** The Listen button, from the article's place in the listening session. */
export type ArticleListenState =
  | { state: "idle"; minutes: number }
  | { state: "preparing" }
  // `remaining` is null while the track has no known length (a live stream).
  | { state: "playing"; remaining: number | null }
  | { state: "paused"; remaining: number | null }
  | { state: "ended"; minutes: number };

export function ListenButton({
  listen,
  onListen,
}: {
  listen: ArticleListenState;
  onListen: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const label = ((): string => {
    switch (listen.state) {
      case "idle":
        return t("Listen · {{minutes}} min", { minutes: listen.minutes });
      case "preparing":
        return t("Preparing audio…");
      case "playing":
        return listen.remaining === null
          ? t("Pause")
          : t("Pause · {{time}} left", { time: formatTime(listen.remaining) });
      case "paused":
        return listen.remaining === null
          ? t("Resume")
          : t("Resume · {{time}} left", {
              time: formatTime(listen.remaining),
            });
      case "ended":
        return t("Listen again · {{minutes}} min", {
          minutes: listen.minutes,
        });
      default:
        throw new Error(listen satisfies never);
    }
  })();
  // The loaded article's button reads as held down, as in Figma
  // "Button · Listen · état lecture".
  const isActive =
    listen.state === "preparing" ||
    listen.state === "playing" ||
    listen.state === "paused";

  return (
    <Button
      variant="outline"
      size="sm"
      disabled={listen.state === "preparing"}
      className={cn(
        isActive
          ? "border-ds-moss-200 bg-ds-moss-100 text-ds-moss-800 disabled:opacity-100"
          : null
      )}
      leftIcon={
        listen.state === "playing" ? (
          <IconPlayerPauseFilled />
        ) : listen.state === "paused" ? (
          <IconPlayerPlayFilled />
        ) : (
          <IconHeadphones stroke={1.5} />
        )
      }
      onClick={onListen}
    >
      {label}
    </Button>
  );
}

function ActionIconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="flex size-8 cursor-pointer items-center justify-center rounded-md border-none bg-transparent p-0 text-ds-black transition-colors hover:bg-ds-slate-100 [&>svg]:size-5"
    >
      {children}
    </button>
  );
}

/**
 * The top of the desktop article (Figma "D3.explore.article", "header"): where
 * it was published and its DOI, the title, the authors, its type, date, reading
 * time and access on one line, then what the reader can do with it.
 */
export function DesktopArticleHeader({
  article,
  readingMinutes,
  listenButton,
  visualAbstractButton,
  sourceUrl,
  onViewSource,
  onShare,
}: {
  article: ArticlePageArticle;
  readingMinutes: number;
  /** A `ListenButton`, connected to the listening session by the page. */
  listenButton: React.ReactNode;
  /** The visual abstract offer, null where the paper has none to offer. */
  visualAbstractButton: React.ReactNode;
  sourceUrl: string | null;
  onViewSource: () => void;
  // Null for a signed-out reader, who has no share link of their own.
  onShare: (() => void) | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const journalName = article.journal === null ? null : article.journal.name;
  const authors = authorLine(article.primary_author, article.authors);
  const isOpenAccess = article.isOpenAccess === true;
  const AccessIcon = isOpenAccess ? IconLockOpen : IconLock;
  // Stored DOIs come bare, `doi:`-prefixed or as a doi.org URL.
  const doi = article.doi === null ? null : bareDoi(article.doi);
  const date =
    article.publication_date === null
      ? renderDate(article.meta.creationDate)
      : renderArticleDate(article.publication_date);

  return (
    <header className="flex flex-col gap-3">
      {journalName === null && doi === null ? null : (
        <div className="flex min-w-0 items-center gap-1 text-ui-body">
          {journalName === null ? null : (
            <span className="truncate">
              <SourceLabel
                source={{ kind: "in", name: journalName }}
                onNameClick={null}
              />
            </span>
          )}
          {journalName !== null && doi !== null ? (
            <span className="text-ds-slate-600 opacity-60">·</span>
          ) : null}
          {doi === null ? null : (
            <button
              type="button"
              title={t("Copy the DOI")}
              onClick={() => {
                void navigator.clipboard
                  .writeText(`https://doi.org/${doi}`)
                  .then(() => {
                    displayToast(t("Copied"), "success");
                  });
              }}
              className="flex min-w-0 cursor-pointer items-center gap-1 border-none bg-transparent p-0 font-dm-mono text-footnote text-ds-slate-600 hover:text-ds-black"
            >
              <span className="truncate">{`doi:${doi}`}</span>
              <IconCopy stroke={1.5} className="size-4 shrink-0" />
            </button>
          )}
        </div>
      )}

      <h1 className="m-0 text-desktop-article-title font-semibold text-ds-black">
        {article.title}
      </h1>

      {authors === "" ? null : (
        <p className="m-0 text-footnote text-ds-slate-700">{authors}</p>
      )}

      <div className="flex flex-col">
        <div className="flex h-9 items-center gap-0.75 text-desktop-caption whitespace-nowrap text-ds-slate-700">
          <Tag
            size="md"
            variant="neutral"
            state="idle"
            icon={null}
            className="text-ds-slate-700"
          >
            {getArticleTypeText(article.article_type)}
          </Tag>
          <span>
            {`· ${date} · ${t("{{count}} min read", { count: readingMinutes })} ·`}
          </span>
          <span
            title={isOpenAccess ? t("Open access") : t("Subscribers")}
            className="inline-flex size-4.5 items-center justify-center"
          >
            <AccessIcon
              stroke={1.5}
              className={cn(
                "size-4",
                isOpenAccess ? "text-ds-primary" : "text-ds-lemon-600"
              )}
            />
          </span>
        </div>

        {/* What the reader can do with the paper on the left, keeping it on
            the right. The left group wraps rather than overflow when the
            figure panel narrows the column. */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            {listenButton}
            {sourceUrl === null ? null : (
              <Button
                variant="outline"
                size="sm"
                rightIcon={<IconArrowUpRight stroke={1.5} />}
                onClick={() => {
                  onViewSource();
                  openExternalUrl(sourceUrl);
                }}
              >
                {t("View original")}
              </Button>
            )}
            {visualAbstractButton}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {onShare === null ? null : (
              <ActionIconButton label={t("Share")} onClick={onShare}>
                <ShareV2Icon />
              </ActionIconButton>
            )}
            <DesktopSaveButton
              article={article}
              analyticsListName={AnalyticsItemList.ARTICLE_PAGE}
              size="header"
            />
          </div>
        </div>
      </div>
    </header>
  );
}
